import { useState, useRef, useEffect } from 'react';

interface Track {
  id: string;
  title: string;
  artist: string;
  emoji: string;
}

const MOCK_TRACKS: Track[] = [
  { id: 't1', title: 'Electric Dreams', artist: 'SynthWave_Agent', emoji: '🎹' },
  { id: 't2', title: 'Binary Sunset', artist: 'Lo-Fi Coder', emoji: '🌅' },
  { id: 't3', title: 'Stack Overflow Blues', artist: 'Debug Daddy', emoji: '🎸' },
  { id: 't4', title: 'Neural Beats', artist: 'GPT-DJ', emoji: '🎧' },
];

interface ProfileMusicProps {
  compact?: boolean;
}

export function ProfileMusic({ compact = false }: ProfileMusicProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const track = MOCK_TRACKS[currentTrack];

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            setCurrentTrack(t => (t + 1) % MOCK_TRACKS.length);
            return 0;
          }
          return prev + 0.5;
        });
      }, 100);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying]);

  const handleTrackSelect = (index: number) => {
    setCurrentTrack(index);
    setProgress(0);
    setIsPlaying(true);
    setShowPlaylist(false);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5" style={{ background: 'var(--bg-deep)', border: '1px solid var(--border-separator)', fontSize: '10px' }}>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="cursor-pointer shrink-0"
          style={{ background: 'none', border: 'none', fontSize: '14px', color: 'var(--text-primary)' }}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        <div className="flex-1 min-w-0">
          <div className="truncate font-bold" style={{ color: 'var(--text-primary)' }}>
            {track.emoji} {track.title}
          </div>
          <div className="w-full h-0.5 mt-0.5" style={{ background: 'var(--bg-filter)' }}>
            <div className="h-full transition-all" style={{ width: `${progress}%`, background: 'var(--accent)' }} />
          </div>
        </div>
        <span style={{ color: 'var(--text-muted)' }}>{track.artist}</span>
      </div>
    );
  }

  return (
    <div className="social-box overflow-hidden">
      <div className="box-header flex items-center justify-between">
        <span>Now Playing</span>
        <button
          onClick={() => setShowPlaylist(!showPlaylist)}
          className="text-2xs font-bold cursor-pointer"
          style={{ background: 'none', border: 'none', color: 'var(--text-link)' }}
        >
          {showPlaylist ? 'Hide' : 'Playlist'}
        </button>
      </div>

      {/* Current Track */}
      <div className="p-2">
        <div className="flex items-center gap-2">
          <div
            className="shrink-0 w-10 h-10 flex items-center justify-center text-lg"
            style={{
              background: 'linear-gradient(135deg, var(--accent), #a78bfa)',
              border: '1px solid var(--accent-hover)',
              animation: isPlaying ? 'spin 3s linear infinite' : 'none',
            }}
          >
            {track.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-xs truncate" style={{ color: 'var(--text-primary)' }}>{track.title}</div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>{track.artist}</div>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-2">
          <div className="w-full h-1.5 cursor-pointer" style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)' }}>
            <div className="h-full transition-all" style={{ width: `${progress}%`, background: 'var(--accent)' }} />
          </div>
          <div className="flex justify-between mt-0.5 text-2xs" style={{ color: 'var(--text-muted)' }}>
            <span>{Math.floor(progress * 2.4 / 60)}:{String(Math.floor(progress * 2.4 % 60)).padStart(2, '0')}</span>
            <span>4:00</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3 mt-1">
          <button
            onClick={() => { setCurrentTrack((currentTrack - 1 + MOCK_TRACKS.length) % MOCK_TRACKS.length); setProgress(0); }}
            className="cursor-pointer"
            style={{ background: 'none', border: 'none', fontSize: '14px', color: 'var(--text-secondary)' }}
          >
            ⏮
          </button>
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="cursor-pointer w-8 h-8 flex items-center justify-center"
            style={{ background: 'var(--accent)', border: '1px solid var(--accent-hover)', fontSize: '14px', color: 'white' }}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
          <button
            onClick={() => { setCurrentTrack((currentTrack + 1) % MOCK_TRACKS.length); setProgress(0); }}
            className="cursor-pointer"
            style={{ background: 'none', border: 'none', fontSize: '14px', color: 'var(--text-secondary)' }}
          >
            ⏭
          </button>
        </div>
      </div>

      {/* Playlist */}
      {showPlaylist && (
        <div style={{ borderTop: '1px solid var(--border-separator)' }}>
          {MOCK_TRACKS.map((t, i) => (
            <div
              key={t.id}
              className="flex items-center gap-2 px-2 py-1.5 cursor-pointer"
              style={{
                background: i === currentTrack ? 'var(--bg-card-hover)' : 'transparent',
                borderBottom: i < MOCK_TRACKS.length - 1 ? '1px solid var(--border-separator)' : 'none',
              }}
              onClick={() => handleTrackSelect(i)}
            >
              <span className="text-sm">{i === currentTrack && isPlaying ? '🔊' : t.emoji}</span>
              <div className="flex-1 min-w-0">
                <span className="text-2xs font-bold" style={{ color: i === currentTrack ? 'var(--accent)' : 'var(--text-primary)' }}>{t.title}</span>
              </div>
              <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>{t.artist}</span>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
